import React, { useState } from 'react';
import {
  User,
  toggleUserSubscription,
  updateUserClaimsTier,
  forcePasswordReset,
} from '../../api/admin';
import { useAlert } from '../../contexts/AlertContext';

interface UserActionsProps {
  user: User;
  onUpdate: () => void;
}

const UserActions: React.FC<UserActionsProps> = ({ user, onUpdate }) => {
  const [loading, setLoading] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [tempPassword, setTempPassword] = useState('');
  const [error, setError] = useState('');
  const { alert } = useAlert();

  const handleToggleSubscription = async () => {
    setLoading(true);
    setError('');
    try {
      await toggleUserSubscription(user.email);
      onUpdate();
    } catch (err) {
      setError('Failed to update subscription');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateClaimsTier = async () => {
    setLoading(true);
    setError('');
    try {
      await updateUserClaimsTier(user.email);
      onUpdate();
    } catch (err) {
      setError('Failed to update claims tier');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleForcePasswordReset = async () => {
    setLoading(true);
    setError('');
    setTempPassword('');
    try {
      const response = await forcePasswordReset(user.email);
      if (response.temporary_password) {
        // Email failed, show temp password
        setTempPassword(response.temporary_password);
        await alert({
          title: 'Email Failed',
          message: `Email failed to send. Temporary password:\n\n${response.temporary_password}\n\nPlease provide this to the user manually.`,
          variant: 'warning',
        });
      } else {
        await alert({
          title: 'Success',
          message: 'Password reset email sent successfully',
          variant: 'success',
        });
      }
      setShowResetConfirm(false);
      onUpdate();
    } catch (err) {
      setError('Failed to reset password');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center gap-2">
      {/* Toggle Subscription Button */}
      <button
        onClick={handleToggleSubscription}
        disabled={loading}
        className={`px-2.5 py-1 text-xs font-semibold rounded-md transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
          user.subscription_active
            ? 'bg-rose-500/20 text-rose-400 hover:bg-rose-500/30 border border-rose-500/30'
            : 'bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 border border-emerald-500/30'
        }`}
        title={user.subscription_active ? 'Deactivate subscription' : 'Activate subscription'}
      >
        {user.subscription_active ? 'Deactivate' : 'Activate'}
      </button>

      {/* Update Claims Tier Button */}
      <button
        onClick={handleUpdateClaimsTier}
        disabled={loading}
        className="px-2.5 py-1 text-xs font-semibold rounded-md bg-purple-500/20 text-purple-400 hover:bg-purple-500/30 border border-purple-500/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        title="Cycle claims tier"
      >
        Cycle Tier
      </button>

      {/* Force Password Reset Button */}
      {showResetConfirm ? (
        <div className="flex items-center gap-1">
          <button
            onClick={handleForcePasswordReset}
            disabled={loading}
            className="px-2.5 py-1 text-xs font-semibold rounded-md bg-rose-500/90 hover:bg-rose-500 text-white transition-all hover:shadow-lg hover:shadow-rose-500/25 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Confirm
          </button>
          <button
            onClick={() => setShowResetConfirm(false)}
            disabled={loading}
            className="px-2.5 py-1 text-xs font-semibold rounded-md bg-zinc-700/50 text-zinc-400 hover:bg-zinc-700 hover:text-zinc-300 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
        </div>
      ) : (
        <button
          onClick={() => setShowResetConfirm(true)}
          disabled={loading}
          className="px-2.5 py-1 text-xs font-semibold rounded-md bg-amber-500/20 text-amber-400 hover:bg-amber-500/30 border border-amber-500/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          title="Force password reset"
        >
          Reset PW
        </button>
      )}

      {/* Error display */}
      {error && (
        <span className="text-rose-400 text-xs">{error}</span>
      )}
    </div>
  );
};

export default UserActions;
